<template>
  <b-navbar-nav>
    <template v-for="item in sortedNavItems">
      <!-- Dropdown items -->
      <b-nav-item-dropdown
          class="mr-4"
          :key="item.label"
          v-if="item.children && item.children.length > 0 && !item.hide"
          :text="item.label"
          :disabled="item.disabled ? item.disabled : false"
      >
        <b-dropdown-item
            :key="child.label"
            v-for="child in sortChildItems(item.children)"
            :href="child.route && child.route.href"
            :target="child.route.target ? child.route.target : '_self'"
            :disabled="child.disabled ? child.disabled : false"
            v-if="!child.hide"
        >
          {{ child.label }}
        </b-dropdown-item>
      </b-nav-item-dropdown>

      <!-- Internal route items -->
      <b-nav-item
          :key="item.label"
          v-else-if="!item.hide && !item.children && item.route && !item.route.href"
          :to="item.route"
          :disabled="item.disabled ? item.disabled : false"
          :active="isActive(item)"
          active-class="active"
      >
        {{ item.label }}
      </b-nav-item>

      <!-- External link items -->
      <b-nav-item
          :key="item.label"
          v-else-if="!item.hide && !item.children && item.route && item.route.href && shouldRenderExternalLink(item)"
          :href="item.route.href"
          :target="item.route.target ? item.route.target : '_self'"
          :disabled="item.disabled ? item.disabled : false"
          :active="isActive(item)"
          active-class="active"
      >
        {{ item.label }}
      </b-nav-item>
    </template>
  </b-navbar-nav>
</template>

<script>
import RoutePermissionsMixin from '@/components/Nav/RoutePermissionsMixin';
import { mapGetters } from 'vuex';
import {canUserAccessBSC, canUserAccessDocumentVault, canUserAccessUrgentActions, canUserAccessClientActions, canUserAccessAssets} from '@/config/features';
import * as permissions from "@/config/permission";

export default {
  name: 'MainNav',
  mixins: [RoutePermissionsMixin],
  data() {
    return {
      routes: [
        {
          label: 'Programme',
          route: { name: 'property-manager' },
          hide: true,
          position: 10,
        },
        {
          label: 'RA Actions',
          route: { name: 'actions-dashboard' },
          active: '/actions',
          hide: true,
          position: 20,
        },
        {
          label: 'Client Actions',
          route: { name: 'clientActions' },
          hide: true,
          position: 30,
        },
        {
          label: 'Urgent Actions',
          route: { href: `${process.env.VUE_APP_MONOREPO_URL}/actions/dashboard?assessment_type=REG7,Generic%20Urgent%20Action` },
          hide: true,
          position: 40,
        },
        {
          label: 'Assets',
          route: { href: this.appendTenure(`${process.env.VUE_APP_RISKHUB_CLOUD_URL}assets/dashboard`) },
          hide: true,
          position: 50,
        },
        {
          label: 'File Sharing',
          route: { name: 'file-sharing' },
          hide: true,
          position: 60,
        },
        {
          label: 'Document Vault',
          route: { href: `${process.env.VUE_APP_MONOREPO_URL}/documents/dashboard` },
          hide: true,
          position: 70,
        },
        {
          label: 'BSC',
          hide: true,
          position: 80,
          children: [
            {
              label: 'BSC Dashboard',
              route: {href: `${process.env.VUE_APP_MONOREPO_URL}/building-safety-cases/dashboard`},
              hide: true,
              // BAU-734: hidden from the menu for now, kept here so it can be switched back on.
              forceHide: true,
              position: 10,
              permissions: [
                {
                  action: permissions.BSC_VIEW_CASE,
                  subject: permissions.PERMISSION_TYPE_BSC,
                },
              ],
            },
            {
              label: 'Case Manager',
              route: {href: `${process.env.VUE_APP_MONOREPO_URL}/building-safety-cases`},
              hide: true,
              position: 20,
              permissions: [
                {
                  action: permissions.BSC_VIEW_CASE,
                  subject: permissions.PERMISSION_TYPE_BSC,
                },
              ],
            },
            {
              label: 'BSC Actions',
              route: {href: `${process.env.VUE_APP_MONOREPO_URL}/actions/dashboard?assessment_type=BSC`},
              hide: true,
              position: 30,
              permissions: [
                {
                  action: permissions.BSC_VIEW_ACTIONS,
                  subject: permissions.PERMISSION_TYPE_BSC,
                },
              ],
            }
          ],
        },
        {
          label: 'Organisation',
          route: { name: 'org' },
          hide: true,
          position: 90,
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      propertyTenure: 'propertyTenure',
      user: 'authUser',
    }),
    assetsPortalEnabled() {
      return parseInt(process.env.VUE_APP_RISKHUB_CLOUD_ENABLED, 10) === 1;
    },
    routesWithPermissions() {
      return this.routes.map(item => {
        const newItem = { ...item };

        if (this.user) {
          if (newItem.label === 'Document Vault' && canUserAccessDocumentVault(this.user)) {
            newItem.hide = false;
          }

          else if (newItem.label === 'Client Actions' && canUserAccessClientActions(this.user)) {
            newItem.hide = false;
          }

          else if (newItem.label === 'Urgent Actions' && canUserAccessUrgentActions(this.user)) {
            newItem.hide = false;
          }

          else if (newItem.label === 'Assets' && canUserAccessAssets(this.user)) {
            newItem.hide = false;
          }

          else if (newItem.label === 'BSC' && canUserAccessBSC(this.user)) {
            newItem.hide = false;
          }

          else if (this.shouldShowNavigationItem(newItem)) {
            newItem.hide = false;
          }

          // If children exist, process them
          if (newItem.children && newItem.children.length) {
            newItem.children = newItem.children.map(child => {
              let access = false;

              if (child.forceHide) {
                return { ...child, hide: true };
              }

              if (child.permissions) {
                for (const p of child.permissions) {
                  if (this.$can(p.action, p.subject)) {
                    access = true;
                    break;
                  }
                }
              }

              return { ...child, hide: !access };
            });
          }
        }

        return newItem;
      });
    },
    sortedNavItems() {
      const DEFAULT_POSITION = 999;

      return [...this.routesWithPermissions].sort((a, b) => {
        const posA = a.position !== undefined ? a.position : DEFAULT_POSITION;
        const posB = b.position !== undefined ? b.position : DEFAULT_POSITION;
        return posA - posB;
      });
    },
  },
  methods: {
    shouldRenderExternalLink(item) {
      if (item.label === 'Assets') {
        return this.assetsPortalEnabled;
      }
      return true;
    },
    isActive(item) {
      if (!item.active) {
        return false;
      }

      return this.$route.path.includes(item.active);
    },
    sortChildItems(children) {
      const DEFAULT_POSITION = 999;
      return [...children].sort((a, b) => {
        const posA = a.position !== undefined ? a.position : DEFAULT_POSITION;
        const posB = b.position !== undefined ? b.position : DEFAULT_POSITION;
        return posA - posB;
      });
    },
    appendTenure(href) {
      return this.propertyTenure !== undefined && this.propertyTenure !== null ? `${href}?tenure=${this.propertyTenure}` : href;
    },
    isExternalLink(item) {
      return item.route && item.route.href && !item.route.name;
    },
    shouldShowNavigationItem(item) {
      return !this.isExternalLink(item) && this.hasPermissionToRoute(item);
    },
  },
};
</script>
